import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiSearch } from "react-icons/fi";
import { API_BASE_URL } from "../utils/constant";
import heroImg from "../assets/Icon/Blog_hero.png"; // Default fallback image

// --- Helper Function to create a clean text excerpt from HTML ---
const createExcerpt = (htmlContent, wordLimit) => {
    if (!htmlContent) return "";
    // Create a temporary div to parse the HTML
    const div = document.createElement("div");
    div.innerHTML = htmlContent;
    // Get the text content, which strips all HTML tags
    const text = div.textContent || div.innerText || "";
    const words = text.trim().split(/\s+/);
    if (words.length <= wordLimit) {
        return text;
    }
    return words.slice(0, wordLimit).join(" ") + "...";
};

// --- Framer Motion Variants ---
const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: "easeOut" },
};

const staggerContainer = {
    animate: {
        transition: {
            staggerChildren: 0.1,
        },
    },
};

// --- Skeleton Components for Loading State ---
const BlogCardSkeleton = () => (
    <div className="bg-white rounded-xl border border-slate-200/80 p-4 space-y-4 animate-pulse">
        <div className="h-48 bg-slate-200 rounded-lg"></div>
        <div className="space-y-2">
            <div className="h-4 bg-slate-200 rounded w-1/4"></div>
            <div className="h-6 bg-slate-200 rounded w-full"></div>
            <div className="h-4 bg-slate-200 rounded w-5/6"></div>
        </div>
        <div className="flex items-center gap-4 pt-2">
            <div className="w-10 h-10 bg-slate-200 rounded-full"></div>
            <div className="flex-grow space-y-2">
                <div className="h-4 bg-slate-200 rounded w-1/3"></div>
                <div className="h-3 bg-slate-200 rounded w-1/4"></div>
            </div>
        </div>
    </div>
);

const FeaturedBlogSkeleton = () => (
    <div className="w-full grid lg:grid-cols-2 gap-8 items-center animate-pulse">
        <div className="h-96 bg-slate-200 rounded-xl"></div>
        <div className="space-y-6">
            <div className="h-6 bg-slate-200 rounded w-1/4"></div>
            <div className="h-10 bg-slate-200 rounded w-full"></div>
            <div className="h-8 bg-slate-200 rounded w-5/6"></div>
            <div className="space-y-2 pt-2">
                <div className="h-4 bg-slate-200 rounded w-full"></div>
                <div className="h-4 bg-slate-200 rounded w-full"></div>
                <div className="h-4 bg-slate-200 rounded w-3/4"></div>
            </div>
            <div className="flex items-center gap-4 pt-4">
                <div className="w-12 h-12 bg-slate-200 rounded-full"></div>
                <div className="flex-grow space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-1/3"></div>
                    <div className="h-3 bg-slate-200 rounded w-1/4"></div>
                </div>
            </div>
        </div>
    </div>
);

// --- Reusable Blog Card Component ---
const BlogCard = ({ blog }) => (
    <motion.div variants={fadeIn} className="h-full">
        <Link to={blog.url} className="group block bg-white rounded-xl border border-slate-200/80 shadow-sm hover:shadow-xl transition-all duration-300 h-full flex flex-col">
            <div className="overflow-hidden rounded-t-xl">
                <img
                    src={blog.imgSrc}
                    alt={blog.title}
                    onError={(e) => { e.target.onerror = null; e.target.src = heroImg; }}
                    className="object-fit w-full h-full transition-transform duration-500 group-hover:scale-105"
                />
            </div>
            <div className="flex flex-col flex-grow p-6">
                <p className="text-sm font-semibold text-blue-600 mb-2">{blog.category}</p>
                <h3 className="mb-3 text-lg font-bold text-slate-800 line-clamp-2">{blog.title}</h3>
                <p className="text-sm text-slate-600 line-clamp-3 flex-grow">{blog.description}</p>
                <div className="border-t border-slate-200 mt-4 pt-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img
                            src={blog.authorImg}
                            onError={(e) => { e.target.onerror = null; e.target.src = heroImg; }}
                            alt={blog.name}
                            className="w-9 h-9 rounded-full object-cover"
                        />
                        <div>
                            <p className="text-sm font-semibold text-slate-700">{blog.name}</p>
                            <p className="text-xs text-slate-500">{blog.date}</p>
                        </div>
                    </div>
                </div>
            </div>
        </Link>
    </motion.div>
);

function DynamicBlogPage() {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("All");

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/blogs`);
                if (!response.ok) throw new Error("Network response was not ok");
                const data = await response.json();
                const mappedBlogs = data.map((item, index) => ({
                    id: item.id,
                    imgSrc: item.featured_image_url || heroImg,
                    title: item.title || "Untitled Blog Post",
                    description: createExcerpt(item.content, 30), // Create excerpt from content
                    name: item.author_name || "Alsol Team",
                    date: new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).format(new Date(item.published_at)),
                    url: `/blog/${item.slug}`,
                    authorImg: item.author_image_url || heroImg,
                    slug: item.slug,
                    category: index % 3 === 0 ? 'Technology' : index % 3 === 1 ? 'Marketing' : 'Business'
                }));
                setBlogs(mappedBlogs);
            } catch (error) {
                console.error("Fetch error:", error);
                setError("Failed to load blog posts. Please try again later.");
            } finally {
                setLoading(false);
            }
        };
        fetchBlogs();
    }, []);

    const categories = useMemo(() => ["All", ...new Set(blogs.map(b => b.category))], [blogs]);

    const filteredBlogs = useMemo(() => {
        return blogs
            .filter(blog => selectedCategory === "All" || blog.category === selectedCategory)
            .filter(blog => blog.title.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [blogs, searchTerm, selectedCategory]);

    const FeaturedBlog = filteredBlogs.length > 0 ? filteredBlogs[0] : null;
    const otherBlogs = filteredBlogs.slice(1);

    return (
        <main className="bg-white">
            <section className="bg-gray-900 border-b border-gray-700">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
    <motion.h1 {...fadeIn} className="text-4xl sm:text-6xl font-extrabold text-white tracking-tight">
      Insights & Ideas
    </motion.h1>
    <motion.p {...fadeIn} transition={{ delay: 0.2 }} className="mt-6 max-w-2xl mx-auto text-lg text-gray-300">
      Your source for the latest trends in technology, marketing, and business strategy from the experts at Alsol.
    </motion.p>

    <motion.div {...fadeIn} transition={{ delay: 0.4 }} className="mt-10 max-w-2xl mx-auto">
      <div className="relative">
        <FiSearch className="absolute top-1/2 left-4 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          placeholder="Search for articles..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full h-14 pl-12 pr-4 bg-gray-800 border border-gray-600 rounded-full text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="mt-4 flex flex-wrap justify-center gap-2">
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${
              selectedCategory === cat
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 hover:bg-gray-700 text-gray-300'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>
    </motion.div>
  </div>
</section>


            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
                {loading ? (
                    <>
                        <FeaturedBlogSkeleton />
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
                            <BlogCardSkeleton />
                            <BlogCardSkeleton />
                            <BlogCardSkeleton />
                        </div>
                    </>
                ) : error ? (
                    <div className="text-center py-10 text-red-500">{error}</div>
                ) : filteredBlogs.length > 0 ? (
                    <motion.div initial="initial" animate="animate" variants={staggerContainer}>
                        <motion.section variants={fadeIn}>
                            <h2 className="text-3xl font-bold text-slate-800 mb-8">Featured Post</h2>
                            <Link to={FeaturedBlog.url} className="group w-full grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                                <div className="overflow-hidden rounded-xl">
                                    <img src={FeaturedBlog.imgSrc} alt={FeaturedBlog.title} className="object-cover w-full h-96 transition-transform duration-500 group-hover:scale-105" />
                                </div>
                                <div className="space-y-4">
                                    <p className="font-semibold text-blue-600">{FeaturedBlog.category}</p>
                                    <h3 className="text-3xl lg:text-4xl font-bold text-slate-800 group-hover:text-blue-600 transition-colors">{FeaturedBlog.title}</h3>
                                    <p className="text-slate-600 line-clamp-4">{FeaturedBlog.description}</p>
                                    <div className="flex items-center gap-4 pt-2">
                                        <img src={FeaturedBlog.authorImg} alt={FeaturedBlog.name} className="w-12 h-12 rounded-full object-cover" />
                                        <div>
                                            <p className="font-semibold text-slate-700">{FeaturedBlog.name}</p>
                                            <p className="text-sm text-slate-500">{FeaturedBlog.date}</p>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        </motion.section>

                        {otherBlogs.length > 0 && (
                            <section className="mt-20">
                                <h2 className="text-3xl font-bold text-slate-800 mb-8">More Articles</h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                                    {otherBlogs.map((blog) => (
                                        <BlogCard key={blog.id} blog={blog} />
                                    ))}
                                </div>
                            </section>
                        )}
                    </motion.div>
                ) : (
                    <div className="text-center py-12">
                        <p className="text-lg text-slate-600">No matching articles found.</p>
                        <p className="text-sm text-slate-500 mt-2">Try adjusting your search or filter.</p>
                    </div>
                )}
            </div>
        </main>
    );
}

export default DynamicBlogPage;
